import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, BookOpen, Quote, Settings, Inbox, LogOut,
  Plus, Pencil, Trash2, Save, ExternalLink, CheckCircle2, X, FileText, Download,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api, formatApiError } from "../../lib/api";
import { WEEKDAYS_FR } from "../../lib/settings";
import { recapPdfUrl, recapDateFr } from "../../lib/recaps";
import { CATEGORIES } from "../../components/EventCard";

const SERMON_THEMES = ["foi", "prière", "famille", "espérance", "Saint-Esprit", "vie chrétienne", "Jésus-Christ", "relations", "évangélisation"];
const TESTIMONIAL_CATEGORIES = ["Jésus a changé ma vie", "Réponses à la prière", "Restauration", "Famille", "Espérance", "Guérison", "Parcours de foi"];

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</label>
    {children}
  </div>
);

const Input = (props) => <input {...props} className="input-field" />;
const Textarea = (props) => <textarea {...props} className="input-field resize-none" rows={3} />;
const Select = ({ children, ...props }) => <select {...props} className="input-field">{children}</select>;

/* ------------------------------ Événements ------------------------------ */
const EMPTY_EVENT = { title: "", category: "special", date: "", location: "", description: "", image: "", link: "", featured: false };

function EventsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => api.get("/admin/events").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, date: form.date.includes("T") ? form.date : `${form.date}T10:00` };
      if (form.id) await api.put(`/admin/events/${form.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Événement enregistré.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Événement supprimé.");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-serif text-xl font-semibold text-obsidian">Événements personnalisés</h2>
        <button data-testid="admin-event-new" onClick={() => setForm({ ...EMPTY_EVENT })} className="btn-primary !px-4 !py-2 !text-xs">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </div>
      <p className="mt-2 text-xs text-stone-500">
        Les rencontres hebdomadaires (Charity Times, Mercredi en prière) sont gérées dans l'onglet Réglages.
      </p>

      {form && (
        <form data-testid="admin-event-form" onSubmit={save} className="card-warm mt-5 space-y-4 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Titre *"><Input data-testid="event-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <Field label="Catégorie">
              <Select data-testid="event-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {Object.entries(CATEGORIES).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
              </Select>
            </Field>
            <Field label="Date et heure *"><Input data-testid="event-date-input" required type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Lieu"><Input data-testid="event-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
          </div>
          <Field label="Description"><Textarea data-testid="event-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Photo (URL, facultatif)"><Input data-testid="event-image-input" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" /></Field>
          <Field label="Lien en ligne (Zoom…, facultatif)"><Input data-testid="event-link-input" value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://zoom.us/j/…" /></Field>
          <label className="flex cursor-pointer items-center gap-3 text-sm text-stone-600">
            <input data-testid="event-featured-checkbox" type="checkbox" checked={!!form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-terracotta" />
            Forte visibilité sur la page d'accueil (événement spécial)
          </label>
          <div className="flex gap-2">
            <button data-testid="event-save-btn" type="submit" className="btn-primary !px-5 !py-2.5 !text-xs"><Save className="h-4 w-4" /> Enregistrer</button>
            <button type="button" onClick={() => setForm(null)} className="btn-outline !px-5 !py-2.5 !text-xs"><X className="h-4 w-4" /> Annuler</button>
          </div>
        </form>
      )}

      <div className="mt-5 space-y-3">
        {items.map((ev) => (
          <div key={ev.id} data-testid={`admin-event-row-${ev.id}`} className="card-warm flex flex-wrap items-center gap-4 p-4">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-obsidian">{ev.title}</p>
              <p className="text-xs text-stone-500">
                {(CATEGORIES[ev.category] || CATEGORIES.special).label} · {ev.date ? format(new Date(ev.date), "d MMM yyyy 'à' HH'h'mm", { locale: fr }) : ""} {ev.location && `· ${ev.location}`}
              </p>
            </div>
            <button data-testid={`admin-event-edit-${ev.id}`} onClick={() => setForm({ ...ev, date: ev.date?.slice(0, 16) })} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-stone-600 hover:border-terracotta hover:text-terracotta" aria-label="Modifier">
              <Pencil className="h-4 w-4" />
            </button>
            <button data-testid={`admin-event-delete-${ev.id}`} onClick={() => remove(ev.id)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-stone-600 hover:border-red-400 hover:text-red-500" aria-label="Supprimer">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!items.length && <p className="py-6 text-center text-sm text-stone-500">Aucun événement personnalisé pour l'instant.</p>}
      </div>
    </div>
  );
}

/* ------------------------------ Prédications ------------------------------ */
const EMPTY_SERMON = { title: "", preacher: "", date: "", verse: "", summary: "", theme: "foi", youtube_url: "", image: "" };

function SermonsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => api.get("/sermons").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/admin/sermons/${form.id}`, form);
      else await api.post("/admin/sermons", form);
      toast.success("Message enregistré.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    await api.delete(`/admin/sermons/${id}`);
    toast.success("Message supprimé.");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-serif text-xl font-semibold text-obsidian">Prédications & messages</h2>
        <button data-testid="admin-sermon-new" onClick={() => setForm({ ...EMPTY_SERMON })} className="btn-primary !px-4 !py-2 !text-xs">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </div>

      {form && (
        <form data-testid="admin-sermon-form" onSubmit={save} className="card-warm mt-5 space-y-4 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Titre *"><Input data-testid="sermon-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <Field label="Prédicateur"><Input data-testid="sermon-preacher-input" value={form.preacher} onChange={(e) => setForm({ ...form, preacher: e.target.value })} /></Field>
            <Field label="Date"><Input data-testid="sermon-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="Thème">
              <Select data-testid="sermon-theme-select" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })}>
                {SERMON_THEMES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
              </Select>
            </Field>
            <Field label="Verset principal"><Input data-testid="sermon-verse-input" value={form.verse} onChange={(e) => setForm({ ...form, verse: e.target.value })} placeholder="Ex. Jean 3:16" /></Field>
            <Field label="Vidéo YouTube (URL)"><Input data-testid="sermon-youtube-input" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://youtube.com/watch?v=…" /></Field>
          </div>
          <Field label="Résumé"><Textarea data-testid="sermon-summary-input" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></Field>
          <Field label="Photo (URL, facultatif)"><Input data-testid="sermon-image-input" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" /></Field>
          <div className="flex gap-2">
            <button data-testid="sermon-save-btn" type="submit" className="btn-primary !px-5 !py-2.5 !text-xs"><Save className="h-4 w-4" /> Enregistrer</button>
            <button type="button" onClick={() => setForm(null)} className="btn-outline !px-5 !py-2.5 !text-xs"><X className="h-4 w-4" /> Annuler</button>
          </div>
        </form>
      )}

      <div className="mt-5 space-y-3">
        {items.map((s) => (
          <div key={s.id} data-testid={`admin-sermon-row-${s.id}`} className="card-warm flex flex-wrap items-center gap-4 p-4">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-obsidian">{s.title}</p>
              <p className="text-xs capitalize text-stone-500">{[s.theme, s.preacher, s.date].filter(Boolean).join(" · ")}</p>
            </div>
            <button data-testid={`admin-sermon-edit-${s.id}`} onClick={() => setForm({ ...s })} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-stone-600 hover:border-terracotta hover:text-terracotta" aria-label="Modifier">
              <Pencil className="h-4 w-4" />
            </button>
            <button data-testid={`admin-sermon-delete-${s.id}`} onClick={() => remove(s.id)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-stone-600 hover:border-red-400 hover:text-red-500" aria-label="Supprimer">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!items.length && <p className="py-6 text-center text-sm text-stone-500">Aucun message publié pour l'instant.</p>}
      </div>
    </div>
  );
}

/* ------------------------------ Témoignages ------------------------------ */
const EMPTY_TESTIMONIAL = { name: "", category: "Parcours de foi", text: "", published: true };

function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => api.get("/admin/testimonials").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/admin/testimonials/${form.id}`, form);
      else await api.post("/admin/testimonials", form);
      toast.success("Témoignage enregistré.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Témoignage supprimé.");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-serif text-xl font-semibold text-obsidian">Témoignages</h2>
        <button data-testid="admin-testimonial-new" onClick={() => setForm({ ...EMPTY_TESTIMONIAL })} className="btn-primary !px-4 !py-2 !text-xs">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </div>
      <p className="mt-2 text-xs text-stone-500">Publiez uniquement des témoignages authentiques, avec l'accord des personnes.</p>

      {form && (
        <form data-testid="admin-testimonial-form" onSubmit={save} className="card-warm mt-5 space-y-4 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Prénom / nom *"><Input data-testid="testimonial-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Catégorie">
              <Select data-testid="testimonial-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {TESTIMONIAL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
          </div>
          <Field label="Témoignage *"><Textarea data-testid="testimonial-text-input" required rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} /></Field>
          <label className="flex cursor-pointer items-center gap-3 text-sm text-stone-600">
            <input data-testid="testimonial-published-checkbox" type="checkbox" checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4 accent-terracotta" />
            Publier sur le site
          </label>
          <div className="flex gap-2">
            <button data-testid="testimonial-save-btn" type="submit" className="btn-primary !px-5 !py-2.5 !text-xs"><Save className="h-4 w-4" /> Enregistrer</button>
            <button type="button" onClick={() => setForm(null)} className="btn-outline !px-5 !py-2.5 !text-xs"><X className="h-4 w-4" /> Annuler</button>
          </div>
        </form>
      )}

      <div className="mt-5 space-y-3">
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-row-${t.id}`} className="card-warm flex flex-wrap items-center gap-4 p-4">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-obsidian">{t.name} <span className="ml-2 text-xs font-normal text-stone-500">{t.category}</span></p>
              <p className="line-clamp-1 text-xs text-stone-500">{t.text}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${t.published ? "bg-olive-soft text-olive-deep" : "bg-sand text-stone-500"}`}>
              {t.published ? "Publié" : "Brouillon"}
            </span>
            <button data-testid={`admin-testimonial-edit-${t.id}`} onClick={() => setForm({ ...t })} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-stone-600 hover:border-terracotta hover:text-terracotta" aria-label="Modifier">
              <Pencil className="h-4 w-4" />
            </button>
            <button data-testid={`admin-testimonial-delete-${t.id}`} onClick={() => remove(t.id)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-stone-600 hover:border-red-400 hover:text-red-500" aria-label="Supprimer">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!items.length && <p className="py-6 text-center text-sm text-stone-500">Aucun témoignage pour l'instant.</p>}
      </div>
    </div>
  );
}

/* ------------------------------ Recap' ------------------------------ */
function RecapsTab() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => api.get("/recaps").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choisissez le fichier PDF du Recap'.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("date", date);
      fd.append("file", file);
      await api.post("/admin/recaps", fd);
      toast.success("Recap' publié sur le site.");
      setTitle("");
      setDate("");
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce Recap' ?")) return;
    await api.delete(`/admin/recaps/${id}`);
    toast.success("Recap' supprimé.");
    load();
  };

  return (
    <div>
      <h2 className="font-serif text-xl font-semibold text-obsidian">Le Recap' de la semaine</h2>
      <p className="mt-2 text-xs text-stone-500">
        Chaque semaine, envoyez ici le PDF du résumé de la prédication du samedi. Le plus récent s'affiche sur la page d'accueil.
      </p>
      <form data-testid="admin-recap-form" onSubmit={submit} className="card-warm mt-5 space-y-4 p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Titre *">
            <Input data-testid="recap-title-input" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Recap' Charity Times du 15.08.2026" />
          </Field>
          <Field label="Date du samedi *">
            <Input data-testid="recap-date-input" required type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
        <Field label="Fichier PDF *">
          <input
            data-testid="recap-file-input"
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-stone-600 file:mr-4 file:rounded-full file:border-0 file:bg-terracotta-soft file:px-5 file:py-2.5 file:text-xs file:font-semibold file:text-terracotta-deep hover:file:bg-terracotta hover:file:text-white"
          />
        </Field>
        <button data-testid="recap-publish-btn" type="submit" disabled={loading} className="btn-primary !px-5 !py-2.5 !text-xs disabled:opacity-60">
          <Plus className="h-4 w-4" />
          {loading ? "Publication…" : "Publier le Recap'"}
        </button>
      </form>

      <div className="mt-6 space-y-3">
        {items.map((r) => (
          <div key={r.id} data-testid={`admin-recap-row-${r.id}`} className="card-warm flex flex-wrap items-center gap-4 p-4">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-terracotta-soft text-terracotta">
              <FileText className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-obsidian">{r.title}</p>
              <p className="text-xs capitalize text-stone-500">{recapDateFr(r.date)}</p>
            </div>
            <a data-testid={`admin-recap-download-${r.id}`} href={recapPdfUrl(r)} target="_blank" rel="noreferrer"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-stone-600 hover:border-terracotta hover:text-terracotta" aria-label="Voir le PDF">
              <Download className="h-4 w-4" />
            </a>
            <button data-testid={`admin-recap-delete-${r.id}`} onClick={() => remove(r.id)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-stone-600 hover:border-red-400 hover:text-red-500" aria-label="Supprimer">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!items.length && <p className="py-6 text-center text-sm text-stone-500">Aucun Recap' publié pour l'instant.</p>}
      </div>
    </div>
  );
}

/* ------------------------------ Réglages ------------------------------ */
function SettingsTab() {
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm(data)).catch(() => {});
  }, []);

  if (!form) return <p className="text-sm text-stone-500">Chargement…</p>;

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, meeting_weekday: Number(form.meeting_weekday) };
      delete payload.recurring;
      await api.put("/admin/settings", payload);
      toast.success("Réglages enregistrés.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form data-testid="admin-settings-form" onSubmit={save} className="space-y-6">
      <h2 className="font-serif text-xl font-semibold text-obsidian">Réglages du site</h2>
      <div className="card-warm space-y-4 p-6">
        <p className="label-eyebrow">Rencontre principale (Charity Times)</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nom de la rencontre"><Input data-testid="settings-meeting-name" value={form.meeting_name} onChange={set("meeting_name")} /></Field>
          <Field label="Jour de la semaine">
            <Select data-testid="settings-weekday" value={form.meeting_weekday} onChange={set("meeting_weekday")}>
              {WEEKDAYS_FR.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </Select>
          </Field>
          <Field label="Heure de début"><Input data-testid="settings-meeting-time" value={form.meeting_time} onChange={set("meeting_time")} placeholder="10h00" /></Field>
          <Field label="Heure d'accueil"><Input data-testid="settings-welcome-time" value={form.welcome_time} onChange={set("welcome_time")} placeholder="9h45" /></Field>
          <Field label="Texte du bouton du menu"><Input data-testid="settings-cta-text" value={form.cta_text} onChange={set("cta_text")} placeholder="Je viens à Charity Times" /></Field>
        </div>
        <Field label="Adresse affichée"><Input data-testid="settings-address" value={form.address} onChange={set("address")} /></Field>
        <Field label="Lien Google Maps / itinéraire"><Input data-testid="settings-maps-url" value={form.maps_url} onChange={set("maps_url")} /></Field>
      </div>
      <div className="card-warm space-y-4 p-6">
        <p className="label-eyebrow">Contact & réseaux</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email de contact"><Input data-testid="settings-contact-email" value={form.contact_email || ""} onChange={set("contact_email")} /></Field>
          <Field label="Téléphone"><Input data-testid="settings-phone" value={form.phone || ""} onChange={set("phone")} /></Field>
          <Field label="Instagram (URL)"><Input data-testid="settings-instagram" value={form.instagram || ""} onChange={set("instagram")} /></Field>
          <Field label="YouTube (URL)"><Input data-testid="settings-youtube" value={form.youtube || ""} onChange={set("youtube")} /></Field>
          <Field label="TikTok (URL)"><Input data-testid="settings-tiktok" value={form.tiktok || ""} onChange={set("tiktok")} /></Field>
          <Field label="WhatsApp (URL)"><Input data-testid="settings-whatsapp" value={form.whatsapp || ""} onChange={set("whatsapp")} /></Field>
        </div>
      </div>
      <div className="card-warm space-y-4 p-6">
        <p className="label-eyebrow">Dons (page Donner)</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="IBAN"><Input data-testid="settings-iban" value={form.iban || ""} onChange={set("iban")} /></Field>
          <Field label="BIC"><Input data-testid="settings-bic" value={form.bic || ""} onChange={set("bic")} /></Field>
        </div>
      </div>
      <button data-testid="settings-save-btn" type="submit" className="btn-primary"><Save className="h-4 w-4" /> Enregistrer les réglages</button>
    </form>
  );
}

/* ------------------------------ Boîte de réception ------------------------------ */
function InboxTab() {
  const [sub, setSub] = useState("prayer");
  const [data, setData] = useState({ prayer: [], visits: [], messages: [], newsletter: [] });

  const load = useCallback(async () => {
    const [p, v, m, n] = await Promise.all([
      api.get("/admin/prayer-requests").catch(() => ({ data: [] })),
      api.get("/admin/visit-requests").catch(() => ({ data: [] })),
      api.get("/admin/messages").catch(() => ({ data: [] })),
      api.get("/admin/newsletter").catch(() => ({ data: [] })),
    ]);
    setData({ prayer: p.data, visits: v.data, messages: m.data, newsletter: n.data });
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (collection, id) => {
    await api.patch(`/admin/inbox/${collection}/${id}/read`);
    load();
  };

  const SUBS = [
    { key: "prayer", label: "Prière", items: data.prayer, collection: "prayer_requests" },
    { key: "visits", label: "Premières visites", items: data.visits, collection: "visit_requests" },
    { key: "messages", label: "Messages", items: data.messages, collection: "contact_messages" },
    { key: "newsletter", label: "Newsletter", items: data.newsletter, collection: null },
  ];
  const current = SUBS.find((s) => s.key === sub);

  return (
    <div>
      <h2 className="font-serif text-xl font-semibold text-obsidian">Boîte de réception</h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {SUBS.map((s) => (
          <button key={s.key} data-testid={`inbox-tab-${s.key}`} onClick={() => setSub(s.key)}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${sub === s.key ? "bg-obsidian text-white" : "border border-line bg-white text-stone-600"}`}>
            {s.label} ({s.items.length})
          </button>
        ))}
      </div>
      <div className="mt-5 space-y-3">
        {sub === "newsletter" ? (
          data.newsletter.map((n, i) => (
            <div key={i} className="card-warm p-4 text-sm text-obsidian">{n.email}
              <span className="ml-2 text-xs text-stone-400">{n.created_at ? format(new Date(n.created_at), "d MMM yyyy", { locale: fr }) : ""}</span>
            </div>
          ))
        ) : current.items.length === 0 ? (
          <p className="py-6 text-center text-sm text-stone-500">Aucun élément pour l'instant.</p>
        ) : (
          current.items.map((item) => (
            <div key={item.id} data-testid={`inbox-item-${item.id}`} className={`card-warm p-5 ${item.read ? "opacity-60" : ""}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-obsidian">
                    {item.first_name} {item.last_name || ""}
                    {item.confidential ? <span className="ml-2 rounded-full bg-terracotta-soft px-2 py-0.5 text-[10px] font-semibold text-terracotta-deep">Confidentiel</span> : null}
                    {!item.read && <span className="ml-2 rounded-full bg-olive-soft px-2 py-0.5 text-[10px] font-semibold text-olive-deep">Nouveau</span>}
                  </p>
                  <p className="mt-0.5 text-xs text-stone-400">
                    {item.created_at ? format(new Date(item.created_at), "d MMM yyyy 'à' HH'h'mm", { locale: fr }) : ""}
                    {item.email && ` · ${item.email}`}
                    {item.phone && ` · ${item.phone}`}
                    {item.contact && ` · ${item.contact}`}
                    {item.date && ` · date envisagée : ${item.date}`}
                    {item.people != null && ` · ${item.people} pers. (${item.adults} ad., ${item.children} enf.)`}
                    {item.wants_callback ? " · souhaite être recontacté" : ""}
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-stone-600">
                    {item.subject && <strong className="text-obsidian">{item.subject} — </strong>}
                    {item.message || ""}
                  </p>
                </div>
                {!item.read && (
                  <button data-testid={`inbox-read-${item.id}`} onClick={() => markRead(current.collection, item.id)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-stone-600 hover:border-olive hover:text-olive-deep">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Marquer lu
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ------------------------------ Vue d'ensemble ------------------------------ */
function OverviewTab({ stats }) {
  const cards = [
    { label: "Demandes de prière non lues", value: stats?.prayer_unread ?? "—", testid: "stat-prayer" },
    { label: "Premières visites à préparer", value: stats?.visits_unread ?? "—", testid: "stat-visits" },
    { label: "Messages non lus", value: stats?.messages_unread ?? "—", testid: "stat-messages" },
    { label: "Événements", value: stats?.events ?? "—", testid: "stat-events" },
    { label: "Prédications", value: stats?.sermons ?? "—", testid: "stat-sermons" },
    { label: "Témoignages", value: stats?.testimonials ?? "—", testid: "stat-testimonials" },
    { label: "Inscrits newsletter", value: stats?.newsletter ?? "—", testid: "stat-newsletter" },
  ];
  return (
    <div>
      <h2 className="font-serif text-xl font-semibold text-obsidian">Vue d'ensemble</h2>
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="card-warm p-6">
            <p className="font-serif text-3xl font-semibold text-terracotta">{c.value}</p>
            <p className="mt-1 text-sm text-stone-600">{c.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------- Page --------------------------------- */
const TABS = [
  { key: "overview", label: "Vue d'ensemble", icon: LayoutDashboard },
  { key: "events", label: "Événements", icon: CalendarDays },
  { key: "sermons", label: "Prédications", icon: BookOpen },
  { key: "recaps", label: "Recap'", icon: FileText },
  { key: "testimonials", label: "Témoignages", icon: Quote },
  { key: "settings", label: "Réglages", icon: Settings },
  { key: "inbox", label: "Boîte de réception", icon: Inbox },
];

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (user) api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [user, tab]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("mdc_admin_token");
    navigate("/admin/login", { replace: true });
  };

  if (user === undefined) {
    return <div className="flex min-h-screen items-center justify-center bg-cream text-sm text-stone-500">Chargement…</div>;
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      <header className="sticky top-0 z-40 border-b border-line bg-cream/90 backdrop-blur-xl">
        <div className="container-site flex h-16 items-center justify-between">
          <img src="/logo.png" alt="La Maison de la Charité" className="h-10 w-auto" />
          <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 sm:block">Back-office</span>
          <div className="flex items-center gap-2">
            <Link data-testid="admin-view-site" to="/" className="btn-outline !px-4 !py-2 !text-xs">
              <ExternalLink className="h-3.5 w-3.5" /> Voir le site
            </Link>
            <button data-testid="admin-logout-btn" onClick={logout} className="btn-outline !px-4 !py-2 !text-xs">
              <LogOut className="h-3.5 w-3.5" /> Déconnexion
            </button>
          </div>
        </div>
        <div className="container-site flex gap-1 overflow-x-auto pb-3">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                tab === t.key ? "bg-obsidian text-white" : "text-stone-600 hover:bg-sand"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="container-site py-10">
        {tab === "overview" && <OverviewTab stats={stats} />}
        {tab === "events" && <EventsTab />}
        {tab === "sermons" && <SermonsTab />}
        {tab === "recaps" && <RecapsTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "settings" && <SettingsTab />}
        {tab === "inbox" && <InboxTab />}
      </main>
    </div>
  );
}
